import { GraphQLResult } from "@aws-amplify/api-graphql";
import { omit } from "lodash";
import {
	CreateResidentInput,
	CreateResidentMutation,
	DeleteResidentInput,
	DeleteResidentMutation,
	GetResidentQuery,
	GetResidentQueryVariables,
	ListResidentsQuery,
	ListResidentsQueryVariables,
	SearchableResidentFilterInput,
	SearchResidentsQuery,
	SearchResidentsQueryVariables,
	UpdateResidentInput,
	UpdateResidentMutation,
} from "../../API";
import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import { Amplify } from "aws-amplify";
import config from "../../amplifyconfiguration.json";
import { generateClient } from "aws-amplify/api";
Amplify.configure(config);

let client = generateClient();

type JSONValue = string | number | boolean | null | JSONObject | JSONArray;

interface JSONObject {
	[key: string]: JSONValue;
}

interface JSONArray extends Array<JSONValue> {}

export default class IntakeService {
	private constructor() {}

	/**
	 * Get an employee object
	 *
	 * @param id id
	 */
	// static async getEmployee(id: string): Promise<GraphQLResult<GetEmployeeQuery>> {
	//     return await API.graphql(graphqlOperation(queries.getEmployee, { id } as GetEmployeeQueryVariables)) as GraphQLResult<GetEmployeeQuery>;
	// }
	/**
	 * Get a resident object
	 *
	 * @param id id
	 */
	static async getResident(
		id: string
	): Promise<GraphQLResult<GetResidentQuery>> {
		return (await client.graphql({
			query: queries.getResident,
			variables: { id } as GetResidentQueryVariables,
		})) as GraphQLResult<GetResidentQuery>;
	}
	/**
	 * Create a new resident object
	 *
	 * @param resident resident object to be created
	 */

	static async createResident(
		resident: CreateResidentInput
	): Promise<GraphQLResult<CreateResidentMutation>> {
		return (await client.graphql({
			query: mutations.createResident,
			variables: {
				input: omit(resident, [
					"createdAt",
					"updatedAt",
				]) as CreateResidentInput,
			},
		})) as GraphQLResult<CreateResidentMutation>;
	}

	static async deleteResident(
		resident: DeleteResidentInput
	): Promise<GraphQLResult<DeleteResidentMutation>> {
		return (await client.graphql({
			query: mutations.deleteResident,
			variables: {
				input: omit(resident, [
					"createdAt",
					"updatedAt",
				]) as DeleteResidentInput,
			},
		})) as GraphQLResult<DeleteResidentMutation>;
	}
	/**
	 * List all residents
	 */

	static async listResidents(
		nextToken?: string
	): Promise<GraphQLResult<ListResidentsQuery>> {
		return (await client.graphql({
			query: queries.listResidents,
			variables: { nextToken } as ListResidentsQueryVariables,
		})) as GraphQLResult<ListResidentsQuery>;
	}

	/**
	 * Update an existing employee object
	 *
	 * @param employee employee object to be updated
	 */
	// static async updateEmployee(employee: UpdateEmployeeInput): Promise<GraphQLResult<UpdateEmployeeMutation>> {
	//     return await API.graphql(graphqlOperation(mutations.updateEmployee,
	//         { input: omit(employee, ['createdAt', 'updatedAt']) })) as GraphQLResult<UpdateEmployeeMutation>;
	// }

	/**
	 * Update an existing resident object
	 *
	 * @param resident resident object to be updated
	 */
	// static async updateResident(resident: UpdateResidentInput): Promise<GraphQLResult<UpdateResidentMutation>> {
	//     return await API.graphql(graphqlOperation(mutations.updateResident,
	//         { input: omit(resident, ['createdAt', 'updatedAt']) })) as GraphQLResult<UpdateResidentMutation>;
	// }

	static async updateResident(
		resident: UpdateResidentInput
	): Promise<GraphQLResult<UpdateResidentMutation>> {
		const input1 = omit(resident, ["createdAt", "updatedAt"]);
		const input = IntakeService.removeTypename(input1);
		console.log("input", JSON.stringify(input, null, 2));
		return (await client.graphql({
			query: mutations.updateResident,
			variables: {
				input: input as UpdateResidentInput,
			},
		})) as GraphQLResult<UpdateResidentMutation>;
	}

	static removeTypename(data: JSONValue): JSONValue {
		if (Array.isArray(data)) {
			// If the data is an array, recursively apply removeTypename to each element
			return data.map(IntakeService.removeTypename);
		} else if (typeof data === "object" && data !== null) {
			// If the data is an object, create a new object without the __typename key
			const newData: JSONObject = {};
			for (const key in data) {
				if (key !== "__typename") {
					newData[key] = IntakeService.removeTypename(data[key]);
				}
			}
			return newData;
		} else {
			// If the data is neither an array nor an object, return it as is
			return data;
		}
	}

	/**
	 * Update an existing externalUser object
	 *
	 * @param externalUser externalUser object to be updated
	 */
	// static async updateExternalUser(externalUser: UpdateExternalUserInput): Promise<GraphQLResult<UpdateExternalUserMutation>> {
	//     return await API.graphql(graphqlOperation(mutations.updateExternalUser,
	//         { input: omit(externalUser, ['createdAt', 'updatedAt']) })) as GraphQLResult<UpdateExternalUserMutation>;
	// }

	/**
	 * Update an existing intake object
	 *
	 * @param intake intake object to be updated
	 */
	// static async updateIntake(intake: UpdateIntakeInput): Promise<GraphQLResult<UpdateIntakeMutation>> {
	//     return await API.graphql(graphqlOperation(mutations.updateIntake,
	//         { input: omit(addDefaultAccess(intake, intake.type), ['createdAt', 'updatedAt']) })) as GraphQLResult<UpdateIntakeMutation>;
	// }

	/**
	 * Get first and last name search filters for the given search string
	 *
	 * @param searchText search text
	 * Replaced SearchableEmployeeFilterInput with SearchableResidentFilterInput[]
	 */
	static getNameSearchFilters(
		searchText: string
	): SearchableResidentFilterInput[] {
		searchText = searchText.toLowerCase();
		const firstNameFilter = searchText.split(" ").map(
			(st) =>
				({
					firstName: { wildcard: "*" + st + "*" },
				} as SearchableResidentFilterInput)
		);
		const lastNameFilter = searchText.split(" ").map(
			(st) =>
				({
					lastName: { wildcard: "*" + st + "*" },
				} as SearchableResidentFilterInput)
		);
		return [...firstNameFilter, ...lastNameFilter];
	}

	/**
	 * Get first and last name search filters for the given search string
	 *
	 * @param searchText search text
	 */
	static getResidentLocationFilters(
		searchText: string
	): SearchableResidentFilterInput[] {
		searchText = searchText.toLowerCase();
		const levelOfCare = searchText.split(" ").map(
			(st) =>
				({
					levelOfCare: { wildcard: "*" + st + "*" },
				} as SearchableResidentFilterInput)
		);
		const building = searchText.split(" ").map(
			(st) =>
				({
					building: { wildcard: "*" + st + "*" },
				} as SearchableResidentFilterInput)
		);
		const floor = searchText.split(" ").map(
			(st) =>
				({
					floor: { wildcard: "*" + st + "*" },
				} as SearchableResidentFilterInput)
		);
		const room = searchText.split(" ").map(
			(st) =>
				({
					room: { wildcard: "*" + st + "*" },
				} as SearchableResidentFilterInput)
		);
		return [...levelOfCare, ...building, ...floor, ...room];
	}

	/**
	 * Search for an employee by first / last name
	 *
	 * @param searchText search text
	 * @param pageSize page size
	 * @param nextToken next token
	 */
	// static async searchEmployees(searchText?: string, filter?: SearchableEmployeeFilterInput, pageSize?: number, nextToken?: string) {
	//     const qVars: SearchEmployeesQueryVariables = {
	//         limit: pageSize || 100,
	//         nextToken
	//     };
	//     if (searchText) {
	//         qVars.filter = {
	//             or: searchText ? this.getNameSearchFilters(searchText.toLowerCase()) : undefined
	//         }
	//     }
	//     if (filter) {
	//         qVars.filter = {
	//             ...qVars.filter,
	//             ...filter
	//         }
	//     }
	//     return await API.graphql(graphqlOperation(queries.searchEmployees, qVars)) as GraphQLResult<SearchEmployeesQuery>;
	// }

	/**
	 * Search for a resident by first / last name
	 *
	 * @param searchText search text
	 * @param pageSize page size
	 * @param nextToken next token
	 */
	static async searchResidents(
		searchText?: string,
		filter?: SearchableResidentFilterInput,
		pageSize?: number,
		nextToken?: string
	): Promise<GraphQLResult<SearchResidentsQuery>> {
		const qVars: SearchResidentsQueryVariables = {
			limit: pageSize || 100,
			nextToken,
		};
		if (searchText) {
			qVars.filter = {
				or: searchText
					? this.getNameSearchFilters(searchText.toLowerCase())
					: undefined,
			};
		}
		if (filter) {
			qVars.filter = {
				...qVars.filter,
				...filter,
			};
		}
		return (await client.graphql({
			query: queries.searchResidents,
			variables: qVars,
		})) as GraphQLResult<SearchResidentsQuery>;
	}

	/**
	 * Search for a External Users by first / last name
	 *
	 * @param searchText search text
	 * @param pageSize page size
	 * @param nextToken next token
	 */
	// static async searchExternalUsers(searchText?: string, filter?: SearchableExternalUserFilterInput, pageSize?: number, nextToken?: string): Promise<GraphQLResult<SearchExternalUsersQuery>> {
	//     const qVars: SearchExternalUsersQueryVariables = {
	//         limit: pageSize || 100,
	//         nextToken
	//     };
	//     if (searchText) {
	//         qVars.filter = {
	//             or: searchText ? this.getNameSearchFilters(searchText.toLowerCase()) : undefined
	//         };
	//     }
	//     if (filter) {
	//         qVars.filter = {
	//             ...qVars.filter,
	//             ...filter
	//         }
	//     }
	//     return await API.graphql(graphqlOperation(queries.searchExternalUsers, qVars)) as GraphQLResult<SearchExternalUsersQuery>;
	// }

	/**
	 * Search for intake forms
	 *
	 * @param searchText search text
	 * @param pageSize page size
	 * @param nextToken next token
	 */
	// static async searchIntakes(searchText?: string, filters?: SearchableIntakeFilterInput[], pageSize?: number, sort?: SearchableIntakeSortInput, nextToken?: string): Promise<GraphQLResult<SearchIntakesQuery>> {
	//     const qVars: SearchIntakesQueryVariables = {
	//         filter: {
	//             or: searchText ? this.getNameSearchFilters(searchText.toLowerCase()) : undefined,
	//             and: filters
	//         },
	//         sort,
	//         limit: pageSize || 5000,
	//         nextToken
	//     };
	//     return await API.graphql(graphqlOperation(queries.searchIntakes, qVars)) as GraphQLResult<SearchIntakesQuery>;
	// }

	// static async getIntake(id: string) {
	//     return await API.graphql(graphqlOperation(queries.getIntake, { id } as GetIntakeQueryVariables)) as GraphQLResult<GetIntakeQuery>;
	// }

	/**
	 * Get any active intake forms (a form exists and episode cleared isn't true) for a person
	 *
	 * @param personId
	 */
	// static async getActiveIntakeByPersonId(personId: string) {
	//     const personIdFilters = [
	//         { personId: { eq: personId }, episodeClosed: { ne: true } }
	//     ] as SearchableIntakeFilterInput[];
	//     const result = await IntakeService.searchIntakes(undefined, personIdFilters);
	//     console.log('Result of intake search: ', result);
	//     const intakes = result.data?.searchIntakes?.items;
	//     if (result.data?.searchIntakes?.items && intakes && intakes.length > 0) {
	//         // There shouldn't be more than 1 intake, but if there is use the one with the latest create date
	//         return intakes.reduce((i1, i2) => new Date(i1?.createdAt as NonNullable<string>)
	//             < new Date(i2?.createdAt as NonNullable<string>) ? i2 : i1);
	//     }
	// }

	/**
	 * Create a new intake object
	 *
	 * @param intake intake object to be created
	 */
	//  static async createIntake(intake: CreateIntakeInput): Promise<GraphQLResult<CreateIntakeMutation>> {
	//      return await API.graphql(graphqlOperation(mutations.createIntake,
	//          { input: omit(addDefaultAccess(intake, intake.type), ['createdAt', 'updatedAt']) })) as GraphQLResult<CreateIntakeMutation>;
	//  }

	// static getIntakeSchema() {
	//     const intakeSchema = ({
	//         intakeQuestionaire: {
	//             title: "Intake Form",
	//             description: "",
	//             groups: [
	//                 {
	//                     title: "Do you have any of these symptoms?",
	//                     questions: [
	//                         {
	//                             id: "Q1",
	//                             text: "Do you feel ill?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q2",
	//                             text: "Do you have a fever?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q3",
	//                             text: "Have you experienced chills?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q4",
	//                             text: "Have you experienced sore throat?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q5",
	//                             text: "Have you experienced body aches?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         }
	//                     ]
	//                 },
	//                 {
	//                     title: "Do you have any of these symptoms?",
	//                     questions: [
	//                         {
	//                             id: "Q7",
	//                             text: "Do you have a cough?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q9",
	//                             text: "Do you have nasal congestion?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q10",
	//                             text: "Do you have shortness of breath?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q11",
	//                             text: "Have you experienced headaches?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q12",
	//                             text: "Have you experienced nausea?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         }
	//                     ]
	//                 },
	//                 {
	//                     title: "Do you have any of these symptoms?",
	//                     questions: [
	//                         {
	//                             id: "Q13",
	//                             text: "Have you experienced any vomiting?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q14",
	//                             text: "Do you have diarrhea?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q15",
	//                             text: "Have you had any loss of smell or taste?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q16",
	//                             text: "Do you have pink eye or conjunctivitis?",
	//                             dataType: QuestionType.YES_NO_DATE,
	//                         },
	//                         {
	//                             id: "Q17",
	//                             text: "Have you been diagnosed with or tested positive for an infectious disease?",
	//                             dataType: QuestionType.MULTISELECT,
	//                             options: ['No', 'COVID-19', 'Flu', 'GI', 'Other']
	//                         }
	//                     ]
	//                 },
	//                 {
	//                     title: "Contact Tracing",
	//                     questions: [
	//                         {
	//                             id: "Q18",
	//                             text: "Have you had close contact with anyone who has symptoms or has tested positive for an infectious disease?",
	//                             dataType: QuestionType.MULTISELECT,
	//                             options: ['No', 'COVID-19', 'Flu', 'GI', 'Other', 'Exposed Externally']
	//                         },
	//                         {
	//                             id: "Q19",
	//                             text: "Have you visited any community neighborhoods in the last 14 days?",
	//                             dataType: QuestionType.MULTISELECT,
	//                             options: ['No', 'IL', 'AL', 'MC', 'SNF']
	//                         },
	//                         {
	//                             id: "Q20",
	//                             text: "What is your current location?",
	//                             dataType: QuestionType.SELECT,
	//                             options: ['IL', 'AL', 'MC', 'SNF', 'Home', 'Hospital', 'Other']
	//                         }
	//                     ]

	//                 },
	//                 {
	//                     title: "Add/Self Reporting",
	//                     questions: [
	//                         {
	//                             id: "Q21",
	//                             text: "Have you traveled outside of the state in the last two weeks?",
	//                             dataType: QuestionType.YES_NO,
	//                         },
	//                         {
	//                             id: "Q22",
	//                             text: "Have you traveled outside of the country in the last two weeks?",
	//                             dataType: QuestionType.YES_NO,
	//                         }
	//                     ]
	//                 },
	//                 {
	//                     title: "Employee",
	//                     questions: [
	//                         {
	//                             id: "Q23",
	//                             text: "Do you work at any other healthcare facilities?",
	//                             dataType: QuestionType.YES_NO,
	//                         },
	//                         {
	//                             id: "Q24",
	//                             text: "Stopped at the gate/sent home?",
	//                             dataType: QuestionType.SELECT,
	//                             options: ['Symptoms', 'Diagnosed with illness', 'Exposure to illness', 'Travel']
	//                         },
	//                         {
	//                             id: "Q25",
	//                             text: "Date stopped at the gate/sent home",
	//                             dataType: QuestionType.DATE,
	//                             defaultValue: 'BLANK'
	//                         },
	//                         {
	//                             id: "Q26",
	//                             text: "Date/Time last worked at Erickson",
	//                             dataType: QuestionType.DATE,
	//                             defaultValue: 'BLANK'
	//                         }
	//                     ]
	//                 }
	//             ]
	//         }
	//     }) as Partial<CreateIntakeInput>;
	//     return intakeSchema;
	// }
	static async mapPersonDataToForm(id: string) {
		// if (personType === PersonType.EMPLOYEE) {
		//     const employeeQuery = await IntakeService.getEmployee(id);
		//     const empData = employeeQuery.data?.getEmployee;
		//     if (empData) {
		//         const empForm = {
		//             type: personType,
		//             personId: empData.id,
		//             community: empData.community,
		//             firstName: empData.firstName,
		//             lastName: empData.lastName,
		//         } as CreateIntakeInput;
		//         // TODO: clean this up
		//         if (empData.middleName) {
		//             empForm.middleName = empData.middleName;
		//         }
		//         if (empData.email) {
		//             empForm.email = empData.email;
		//         }
		//         if (empData.title) {
		//             empForm.title = empData.title;
		//         }
		//         if (empData.jobCode) {
		//             empForm.jobCode = empData.jobCode;
		//         }
		//         if (empData.managerID) {
		//             empForm.managerID = empData.managerID;
		//         }
		//         if (empData.managerEmail) {
		//             empForm.managerEmail = empData.managerEmail;
		//         }
		//         if (empData.departmentID) {
		//             empForm.departmentID = empData.departmentID;
		//         }
		//         if (empData.departmentName) {
		//             empForm.departmentName = empData.departmentName;
		//         }
		//         if (empData.phone) {
		//             empForm.phone = [empData.phone];
		//         }
		//         return empForm;
		//     }
		// }
		// else if (personType === PersonType.RESIDENT) {
		const residentQuery = await IntakeService.getResident(id);
		const resData = residentQuery.data?.getResident;
		if (resData) {
			const resForm = {
				personId: resData.id,
				community: resData.community,
				firstName: resData.firstName,
				lastName: resData.lastName,
			} as any;
			// CreateIntakeInput replaced by any
			if (resData.middleName) {
				resForm.middleName = resData.middleName;
			}
			console.log(resForm);
			return resForm;
		}
		// }

		// else if (personType === PersonType.EXTERNALUSER) {
		//     const residentQuery = await IntakeService.getOther(personId);
		//     const externalUserData = residentQuery.data?.getExternalUser;
		//     if (externalUserData) {
		//         const externalUserForm = {
		//             type: 'other',
		//             personId: externalUserData.id,
		//             community: externalUserData.community,
		//             firstName: externalUserData.firstName,
		//             lastName: externalUserData.lastName,
		//         } as CreateIntakeInput;
		//         // TODO: clean this up
		//         if (externalUserData.middleName) {
		//             externalUserForm.middleName = externalUserData.middleName;
		//         }
		//         return externalUserForm;
		//     }
		// }
		// else {
		//     EventService.log('error', `Unknown person type ${personType}`);
		// }
	}
	// static async submitIntakeForm(personId: string) {
	//     const activeIntake = await IntakeService.getActiveIntakeByPersonId(personId);
	//     if (activeIntake) {
	//         EventService.log('debug', 'Active intake already exists');
	//         return activeIntake
	//     }
	//     const personData = await this.mapPersonDataToForm(personType, personId);
	//     const monitoringType = MonitoringType.CONTACT
	//     // ? MonitoringType.CONTACT
	//     const intakeData = { ...this.getIntakeSchema(), ...personData, monitoringType } as CreateIntakeInput;
	//     const intakeMutation = await IntakeService.createIntake(intakeData as CreateIntakeInput);
	//     return intakeMutation.data?.createIntake;
	// }

	// static async submitIntakeFormWithRecordType(personType: PersonType, personId: string, mType: MonitoringType) {
	//     const activeIntake = await IntakeService.getActiveIntakeByPersonId(personId);
	//     if (activeIntake) {
	//         EventService.log('debug', 'Active intake already exists');
	//         return activeIntake
	//     }
	//     const personData = await this.mapPersonDataToForm(personType, personId);
	//     const monitoringType = mType
	//     // ? MonitoringType.CONTACT
	//     const intakeData = { ...this.getIntakeSchema(), ...personData, monitoringType } as CreateIntakeInput;
	//     const intakeMutation = await IntakeService.createIntake(intakeData as CreateIntakeInput);
	//     return intakeMutation.data?.createIntake;
	// }
}
